import { WordMat } from '@/common/types/wordMat';
import {
  CopySimpleIcon,
  DotsThreeVerticalIcon,
  PencilSimpleIcon,
  PresentationIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shadcn/components/dropdown-menu';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WordMatPreview from './WordMatPreview';

interface WordMatCardProps {
  wordMat: WordMat;
  onPresent: () => void;
  onCopy: () => void;
  onConfirmDelete: () => void;
}

export default function WordMatCard({
  wordMat,
  onPresent,
  onCopy,
  onConfirmDelete,
}: WordMatCardProps) {
  const navigate = useNavigate();
  const [isDropdownOpen, setDropdownOpen] = useState(false);

  function handleNavigateToWordMat() {
    navigate(`/dashboard/wordmats/${wordMat.id}`);
  }

  return (
    <div
      className="group relative bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors"
      onClick={handleNavigateToWordMat}
    >
      {/* Preview area */}
      <WordMatPreview wordMat={wordMat} className="h-32" />

      {/* Card footer */}
      <div className="p-3 border-t border-gray-100">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-sm text-gray-900 truncate">
              {wordMat.name}
            </h3>
            <span className="text-xs text-gray-500">
              {dayjs(wordMat.updatedAt).fromNow()}
            </span>
          </div>

          {/* Actions dropdown */}
          <DropdownMenu open={isDropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <DotsThreeVerticalIcon size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem
                className="flex gap-2"
                onClick={() => {
                  setDropdownOpen(false);
                  onPresent();
                }}
              >
                <PresentationIcon size={16} /> Present
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex gap-2"
                onClick={() => {
                  setDropdownOpen(false);
                  onCopy();
                }}
              >
                <CopySimpleIcon size={16} /> Make a copy
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="flex gap-2"
                onClick={() => {
                  setDropdownOpen(false);
                  handleNavigateToWordMat();
                }}
              >
                <PencilSimpleIcon size={16} /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex gap-2 text-red-600"
                onClick={() => {
                  setDropdownOpen(false);
                  onConfirmDelete();
                }}
              >
                <TrashIcon size={16} /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
